'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../utils/axios-config';
import { API_ENDPOINTS } from '../config/api-config';
import { notify } from '../stores';

/**
 * Hook for submitting an appeal
 */
export function useSubmitAppeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ moderationActionId, reason, additionalEvidence }) => {
      const response = await axiosInstance.post(API_ENDPOINTS.APPEALS.SUBMIT, {
        moderationActionId,
        reason,
        additionalEvidence,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appeals', 'my'] });
      notify.success('Appeal submitted successfully');
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Failed to submit appeal';
      notify.error(message);
    },
  });
}

/**
 * Hook for getting user's own appeals
 */
export function useMyAppeals(options = {}) {
  const { page = 1, limit = 10, status = null } = options;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['appeals', 'my', { page, limit, status }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (status) params.append('status', status);

      const response = await axiosInstance.get(
        `${API_ENDPOINTS.APPEALS.MY_APPEALS}?${params.toString()}`
      );
      return response.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

  return {
    appeals: data?.appeals || [],
    pagination: data?.pagination || { page: 1, limit: 10, total: 0, pages: 0 },
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for getting a single appeal
 */
export function useAppeal(appealId) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['appeals', appealId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API_ENDPOINTS.APPEALS.GET_BY_ID(appealId)
      );
      return response.data;
    },
    enabled: !!appealId,
  });

  return {
    appeal: data?.appeal || null,
    isLoading,
    error,
  };
}

/**
 * Hook for withdrawing an appeal
 */
export function useWithdrawAppeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (appealId) => {
      const response = await axiosInstance.post(
        API_ENDPOINTS.APPEALS.WITHDRAW(appealId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appeals'] });
      notify.success('Appeal withdrawn successfully');
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Failed to withdraw appeal';
      notify.error(message);
    },
  });
}

// ============================================
// ADMIN HOOKS
// ============================================

/**
 * Hook for admin to get all appeals
 */
export function useAdminAppeals(options = {}) {
  const {
    page = 1,
    limit = 20,
    status = null,
    sortBy = 'createdAt',
    sortOrder = 'desc',
  } = options;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['appeals', 'admin', { page, limit, status, sortBy, sortOrder }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (status) params.append('status', status);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      const response = await axiosInstance.get(
        `${API_ENDPOINTS.APPEALS.ADMIN.GET_ALL}?${params.toString()}`
      );
      return response.data;
    },
    staleTime: 15000,
    refetchOnWindowFocus: true,
  });

  return {
    appeals: data?.appeals || [],
    stats: data?.stats || {},
    pagination: data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 },
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for starting appeal review
 */
export function useStartReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (appealId) => {
      const response = await axiosInstance.post(
        API_ENDPOINTS.APPEALS.ADMIN.REVIEW(appealId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appeals'] });
      notify.success('Review started');
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Failed to start review';
      notify.error(message);
    },
  });
}

/**
 * Hook for approving an appeal
 */
export function useApproveAppeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appealId, reviewNotes, reverseAction, reversalNotes }) => {
      const response = await axiosInstance.post(
        API_ENDPOINTS.APPEALS.ADMIN.APPROVE(appealId),
        { reviewNotes, reverseAction, reversalNotes }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['appeals'] });
      const wasReversed = data.appeal?.reversalDetails?.wasReversed;
      notify.success(
        wasReversed
          ? 'Appeal approved and original action reversed'
          : 'Appeal approved'
      );
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Failed to approve appeal';
      notify.error(message);
    },
  });
}

/**
 * Hook for rejecting an appeal
 */
export function useRejectAppeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appealId, reviewNotes, rejectionReason }) => {
      const response = await axiosInstance.post(
        API_ENDPOINTS.APPEALS.ADMIN.REJECT(appealId),
        { reviewNotes, rejectionReason }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appeals'] });
      notify.success('Appeal rejected');
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Failed to reject appeal';
      notify.error(message);
    },
  });
}
